// Single source of truth for "how many hours is duty shift X worth?" (#1597).
//
// `hours` on a duty shift doc is DERIVED from the shift's own start/end window,
// but it was stored as a plain field and written only when the drawer's form
// marked it dirty — which it never did. So a shift whose window was edited kept
// the hours of the window it used to have, and nothing in the product displayed
// the number, so a wrong one could not be seen. Production carried 16 such
// shifts across three departments; one of them exported 40 hours for a 24-hour
// standby to payroll, for four people, inside an approved period.
//
// The window is the truth and `hours` is a cache of it, so every reader
// recomputes here instead of trusting the stored field. That makes a stale
// value unpayable even on docs written before the drawer was fixed, with no
// backfill: every affected shift in production has a usable window.
//
// `hours` is still written (exports, the api and older docs all read it, and a
// shift whose window is somehow unusable has nothing else to fall back on), but
// it is no longer what anyone prices against.

// Duty-shift windows reach this from three places in three shapes: raw
// Firestore Timestamps (the api's payroll scan does a direct `.get()`), Moment
// objects (both SPAs, via processRawDocs), and JS Dates or ISO strings (tests,
// bin scripts, hand-written docs). Timestamps and Moments both expose
// `.toDate()`, so that one branch covers them.
const toDate = value => {
    if (value == null) return null;
    const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const toNumber = value => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

// Hours between two instants, at quarter-hour granularity, rounded UP, never
// negative. This is the rule the duty shift drawer has always applied when it
// derived the field; keeping it here rather than in the drawer is what lets a
// reader arrive at the same answer the form would have shown.
//
// Deliberately an ABSOLUTE difference, not a wall-clock one: a 24h shift across
// a DST boundary is 23 or 25 real hours, and the crew stood the real hours.
const calculateDutyShiftHours = (start, end) => {
    const from = toDate(start);
    const to = toDate(end);
    if (!from || !to || to < from) return null;
    return Math.max(Math.ceil(((to - from) / 3600000) * 4) / 4, 0);
};

// The payable hours for one duty shift.
//
// Prefers the window, because the window is what the shift actually was. Falls
// back to the stored `hours` only when the window can't be read at all — a doc
// with no dates has nothing better to offer, and dropping it to 0 there would
// silently unpay someone rather than surfacing bad data.
//
// Returns 0 (not null) when neither is usable, so callers can sum the result
// without guarding every term.
const resolveDutyShiftHours = shift => {
    if (!shift) return 0;

    const calculated = calculateDutyShiftHours(shift.startDate, shift.endDate);
    if (calculated !== null) return calculated;

    const stored = toNumber(shift.hours);
    return stored === null ? 0 : stored;
};

module.exports = {
    calculateDutyShiftHours,
    resolveDutyShiftHours
};
